import { useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, TextField, Typography, Stack, Checkbox, FormControlLabel,
    FormControl, Select, MenuItem, IconButton, Button
} from '@mui/material';
import * as api from './locationsApi';
import type {
    Location, User, AddressType, ContactInfoType, Address, ContactInfo, Pricelist
} from './locationsApi';
import { sx13, FormRow, DetailViewShell } from '../common/detailView';
import { useDetailView } from '../common/useDetailView';

interface Props {
    locationId: number | null;
    allowUpdate: boolean;
    isAllowedToSetAutoOrder: boolean;
}

export function LocationView({ locationId, allowUpdate, isAllowedToSetAutoOrder }: Props) {
    const { t } = useTranslation();
    const detail = useDetailView<Location>();

    const [users, setUsers] = useState<User[]>([]);
    const [addressTypes, setAddressTypes] = useState<AddressType[]>([]);
    const [contactInfoTypes, setContactInfoTypes] = useState<ContactInfoType[]>([]);
    const [userLocations, setUserLocations] = useState<number[]>([]);
    const [pricelists, setPricelists] = useState<Pricelist[]>([]);

    const [newAddressTypeId, setNewAddressTypeId] = useState<number | ''>('');
    const [newContactInfoTypeId, setNewContactInfoTypeId] = useState<number | ''>('');

    const load = useCallback(async () => {
        if (locationId === null) {
            detail.setLoading(false);
            return;
        }
        await detail.withLoading(async () => {
            const [loc, usrs, addrTypes, ciTypes, userLocs, pls] = await Promise.all([
                api.getLocation(locationId),
                api.getUsers(),
                api.getAddressTypes(),
                api.getContactInfoTypes(),
                api.getUserLocations(),
                api.getPricelists(),
            ]);
            loc.addresses = loc.addresses ?? [];
            loc.contactInfos = loc.contactInfos ?? [];
            loc.users = loc.users ?? [];
            detail.setItem(loc);
            setUsers(usrs);
            setAddressTypes(addrTypes);
            setContactInfoTypes(ciTypes);
            setUserLocations(userLocs ?? []);
            setPricelists([{ id: 0, pricelistName: '-' }, ...(pls ?? [])]);
            detail.setIsEditing(false);
        });
    }, [locationId]);

    useEffect(() => { load(); }, [load]);

    const item = detail.item;

    const allowSaveLocation =
        allowUpdate && item != null && (userLocations.length === 0 || userLocations.includes(item.id));

    const startEdit = () => {
        detail.startEdit();
    };

    const cancelEdit = () => {
        detail.cancelEdit();
        setNewAddressTypeId('');
        setNewContactInfoTypeId('');
    };

    const save = async () => {
        if (!item) return;
        await detail.withSaving(async () => {
            const payload: Location = {
                ...item,
                addresses: item.addresses.map(a => a.id < 0 ? { ...a, id: 0 } : a),
                contactInfos: item.contactInfos.map(c => c.id < 0 ? { ...c, id: 0 } : c),
            };
            const response = await api.updateLocation(payload);
            response.addresses = response.addresses ?? [];
            response.contactInfos = response.contactInfos ?? [];
            response.users = response.users ?? [];
            detail.setItem(response);
            detail.setIsEditing(false);
        });
    };

    const updateAddress = (id: number, patch: Partial<Address>) => {
        if (!item) return;
        detail.update('addresses', item.addresses.map(a => a.id === id ? { ...a, ...patch } : a));
    };
    const deleteAddress = (id: number) => {
        if (!item) return;
        detail.update('addresses', item.addresses.filter(a => a.id !== id));
    };
    const addAddress = () => {
        if (!item) return;
        const type = addressTypes.find(t => t.id === newAddressTypeId);
        if (!type) return;
        const addr: Address = { id: -Date.now(), addressType: type };
        detail.update('addresses', [...item.addresses, addr]);
        setNewAddressTypeId('');
    };

    const updateContactInfo = (id: number, patch: Partial<ContactInfo>) => {
        if (!item) return;
        detail.update('contactInfos', item.contactInfos.map(c => c.id === id ? { ...c, ...patch } : c));
    };
    const deleteContactInfo = (id: number) => {
        if (!item) return;
        detail.update('contactInfos', item.contactInfos.filter(c => c.id !== id));
    };
    const addContactInfo = () => {
        if (!item) return;
        const type = contactInfoTypes.find(t => t.id === newContactInfoTypeId);
        if (!type) return;
        const ci: ContactInfo = { id: -Date.now(), contactInfoType: type, value: '' };
        detail.update('contactInfos', [...item.contactInfos, ci]);
        setNewContactInfoTypeId('');
    };

    const yesNo = (v: boolean) => v ? t('common.true') : t('common.false');

    return (
        <DetailViewShell<Location>
            title={item?.locationName ?? ''}
            isEditing={detail.isEditing}
            isNew={false}
            saving={detail.saving}
            canSave={!!allowSaveLocation}
            loading={detail.loading}
            error={detail.error}
            item={item}
            onEdit={allowSaveLocation ? startEdit : () => {}}
            onSave={save}
            onCancel={cancelEdit}
            onClearError={() => detail.setError(null)}
            noItemMessage="No location selected."
        >
            {(loc) => {
                const startDate = loc.startDate ? new Date(loc.startDate).toISOString().slice(0, 10) : '';
                const activeUsers = [...loc.users]
                    .filter(u => u.isActive !== false)
                    .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));

                return (<>
                    <FormRow label={t('location.name')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx13}>{loc.locationName}</Typography>
                        ) : (
                            <TextField size="small" fullWidth
                                value={loc.locationName ?? ''}
                                onChange={e => detail.update('locationName', e.target.value)} />
                        )}
                    </FormRow>

                    <FormRow label={t('location.locationnumber')}>
                        <Typography sx={sx13}>{loc.locationNumber}</Typography>
                    </FormRow>

                    <FormRow label={t('user.isactive')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx13}>{yesNo(loc.isActive)}</Typography>
                        ) : (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={!!loc.isActive}
                                        onChange={e => detail.update('isActive', e.target.checked)}
                                    />
                                }
                                label=""
                            />
                        )}
                    </FormRow>

                    <FormRow label={t('location.shouldGenerateAutoOrders')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx13}>{yesNo(loc.shouldGenerateAutoOrders)}</Typography>
                        ) : (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={!!loc.shouldGenerateAutoOrders}
                                        disabled={!isAllowedToSetAutoOrder}
                                        onChange={e => detail.update('shouldGenerateAutoOrders', e.target.checked)}
                                    />
                                }
                                label=""
                            />
                        )}
                    </FormRow>

                    <FormRow label={t('location.type')}>
                        <Typography sx={sx13}>{loc.locationType?.description}</Typography>
                    </FormRow>

                    <FormRow label={t('location.category')}>
                        <Typography sx={sx13}>{loc.locationCategory?.category}</Typography>
                    </FormRow>

                    {loc.salesChannel && (
                        <FormRow label={t('location.saleschannel')}>
                            <Typography sx={sx13}>{loc.salesChannel.salesChannelDescription}</Typography>
                        </FormRow>
                    )}

                    {loc.resellerCustomer && (
                        <FormRow label={t('location.resellerCustomer')}>
                            <Typography sx={sx13}>
                                {loc.resellerCustomer.socialSecurityNumber}{' '}
                                {loc.resellerCustomer.firstName}{' '}
                                {loc.resellerCustomer.lastName}
                            </Typography>
                        </FormRow>
                    )}

                    {loc.resellerCostPriceFactor != null && (
                        <FormRow label={t('location.resellerCostPriceFactor')}>
                            <Typography sx={sx13}>{loc.resellerCostPriceFactor}</Typography>
                        </FormRow>
                    )}

                    <FormRow label={t('location.storemanager')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx13}>
                                {loc.storeManager
                                    ? `${loc.storeManager.firstName} ${loc.storeManager.lastName}`
                                    : t('location.storemanager.none')}
                            </Typography>
                        ) : (
                            <FormControl size="small" fullWidth>
                                <Select
                                    value={loc.storeManager?.id ?? ''}
                                    onChange={e => {
                                        const u = users.find(x => x.id === e.target.value);
                                        detail.update('storeManager', u ?? null);
                                    }}
                                    displayEmpty
                                >
                                    <MenuItem value=""><em>-</em></MenuItem>
                                    {users.map(u => (
                                        <MenuItem key={u.id} value={u.id}>{u.firstName} {u.lastName}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </FormRow>

                    <FormRow label={t('location.squaremeters')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx13}>{loc.squareMeters}</Typography>
                        ) : (
                            <TextField size="small" type="number" fullWidth
                                value={loc.squareMeters}
                                onChange={e => detail.update('squareMeters', e.target.value === '' ? 0 : Number(e.target.value))} />
                        )}
                    </FormRow>

                    <FormRow label={t('location.startdate')}>
                        <Typography sx={sx13}>{startDate}</Typography>
                    </FormRow>

                    <FormRow label={t('location.organizationnumber')}>
                        <Typography sx={sx13}>{loc.organizationNumber}</Typography>
                    </FormRow>

                    <FormRow label={t('location.resellerPriceList')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx13}>{loc.resellerPriceList?.pricelistName}</Typography>
                        ) : (
                            <FormControl size="small" fullWidth>
                                <Select
                                    value={loc.resellerPriceList?.id ?? 0}
                                    onChange={e => {
                                        const pl = pricelists.find(p => p.id === e.target.value);
                                        detail.update('resellerPriceList', pl && pl.id !== 0 ? pl : null);
                                    }}
                                >
                                    {pricelists.map(p => (
                                        <MenuItem key={p.id} value={p.id}>{p.pricelistName}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </FormRow>

                    <Box />

                    {/* Addresses */}
                    <Stack spacing={1}>
                        {loc.addresses.map(addr => (
                            <FormRow key={addr.id} label={addr.addressType?.typeName ?? ''}>
                                <AddressBlock
                                    address={addr}
                                    isEditing={detail.isEditing}
                                    onUpdate={patch => updateAddress(addr.id, patch)}
                                    onDelete={() => deleteAddress(addr.id)}
                                />
                            </FormRow>
                        ))}
                        {detail.isEditing && addressTypes.length > 0 && (
                            <FormRow label="">
                                <Box display="flex" gap={1}>
                                    <FormControl size="small" sx={{ flex: 1 }}>
                                        <Select
                                            value={newAddressTypeId}
                                            onChange={e => setNewAddressTypeId(e.target.value as number)}
                                            displayEmpty
                                        >
                                            <MenuItem value=""><em>{t('address.chooseType')}</em></MenuItem>
                                            {addressTypes.map(at => (
                                                <MenuItem key={at.id} value={at.id}>{at.typeName}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Button size="small" variant="contained" color="secondary"
                                        onClick={addAddress} disabled={newAddressTypeId === ''}>
                                        +
                                    </Button>
                                </Box>
                            </FormRow>
                        )}
                    </Stack>

                    <Box />

                    {/* Contact info */}
                    <Stack spacing={1}>
                        {loc.contactInfos.map(ci => (
                            <FormRow key={ci.id} label={ci.contactInfoType?.typeName ?? ''}>
                                <ContactInfoBlock
                                    contactInfo={ci}
                                    isEditing={detail.isEditing}
                                    onUpdate={patch => updateContactInfo(ci.id, patch)}
                                    onDelete={() => deleteContactInfo(ci.id)}
                                />
                            </FormRow>
                        ))}
                        {detail.isEditing && contactInfoTypes.length > 0 && (
                            <FormRow label="">
                                <Box display="flex" gap={1}>
                                    <FormControl size="small" sx={{ flex: 1 }}>
                                        <Select
                                            value={newContactInfoTypeId}
                                            onChange={e => setNewContactInfoTypeId(e.target.value as number)}
                                            displayEmpty
                                        >
                                            <MenuItem value=""><em>{t('contactInfo.chooseType')}</em></MenuItem>
                                            {contactInfoTypes.map(ct => (
                                                <MenuItem key={ct.id} value={ct.id}>{ct.typeName}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Button size="small" variant="contained" color="secondary"
                                        onClick={addContactInfo} disabled={newContactInfoTypeId === ''}>
                                        +
                                    </Button>
                                </Box>
                            </FormRow>
                        )}
                    </Stack>

                    <Box />

                    <FormRow label={t('location.users')}>
                        <Box>
                            {activeUsers.map(u => (
                                <Typography sx={sx13} key={u.id}>{u.firstName} {u.lastName}</Typography>
                            ))}
                        </Box>
                    </FormRow>
                </>);
            }}
        </DetailViewShell>
    );
}

function AddressBlock({
    address, isEditing, onUpdate, onDelete
}: {
    address: Address;
    isEditing: boolean;
    onUpdate: (patch: Partial<Address>) => void;
    onDelete: () => void;
}) {
    if (!isEditing) {
        const parts = [
            [address.addressLine1, address.addressLine2, address.addressLine3].filter(Boolean).join(' '),
            [address.postalCode, address.city].filter(Boolean).join(' '),
            address.country,
        ].filter(Boolean).join(', ');
        return <Typography sx={sx13}>{parts}</Typography>;
    }

    return (
        <Box display="flex" gap={1} alignItems="flex-start">
            <Stack spacing={0.5} flex={1}>
                <TextField size="small" fullWidth placeholder="Line 1"
                    value={address.addressLine1 ?? ''}
                    onChange={e => onUpdate({ addressLine1: e.target.value })} />
                <TextField size="small" fullWidth placeholder="Line 2"
                    value={address.addressLine2 ?? ''}
                    onChange={e => onUpdate({ addressLine2: e.target.value })} />
                <TextField size="small" fullWidth placeholder="Line 3"
                    value={address.addressLine3 ?? ''}
                    onChange={e => onUpdate({ addressLine3: e.target.value })} />
                <Box display="flex" gap={1}>
                    <TextField size="small" sx={{ width: 120 }} placeholder="Postal code"
                        value={address.postalCode ?? ''}
                        onChange={e => onUpdate({ postalCode: e.target.value })} />
                    <TextField size="small" fullWidth placeholder="City"
                        value={address.city ?? ''}
                        onChange={e => onUpdate({ city: e.target.value })} />
                </Box>
                <TextField size="small" fullWidth placeholder="Country"
                    value={address.country ?? ''}
                    onChange={e => onUpdate({ country: e.target.value })} />
            </Stack>
            <IconButton size="small" color="error" onClick={onDelete}>×</IconButton>
        </Box>
    );
}

function ContactInfoBlock({
    contactInfo, isEditing, onUpdate, onDelete
}: {
    contactInfo: ContactInfo;
    isEditing: boolean;
    onUpdate: (patch: Partial<ContactInfo>) => void;
    onDelete: () => void;
}) {
    if (!isEditing) {
        return <Typography sx={sx13}>{contactInfo.value}</Typography>;
    }
    return (
        <Box display="flex" gap={1} alignItems="center">
            <TextField size="small" fullWidth
                value={contactInfo.value ?? ''}
                onChange={e => onUpdate({ value: e.target.value })} />
            <IconButton size="small" color="error" onClick={onDelete}>×</IconButton>
        </Box>
    );
}
